import { Loader2, Mic, RefreshCw, SendHorizontal } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Markdown } from '@apps/web/components/ui/markdown';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useVoiceInput } from '@apps/web/hooks/use_voice_input';
import { getApiClient } from '@apps/web/lib/api_client';
import { stripCodeFences } from '@apps/web/lib/memory_markdown';
import {
    useCaregiverMemory,
    useClientMemory,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

const MAX_INSTRUCTION_LENGTH = 500;

const MEMORY_POLL_INTERVAL_MS = 1_000;
const MEMORY_POLL_TIMEOUT_MS = 60_000;

const ENTITY_CONFIG = {
    caregiver: {
        inputId: 'caregiver-memory-instruction',
        placeholder:
            'Tell Phoebe what to remember or forget about this caregiver',
        emptyText:
            'No memory available yet. Phoebe learns about caregivers during SMS and voice conversations, or you can add notes below.',
    },
    client: {
        inputId: 'client-memory-instruction',
        placeholder: 'Tell Phoebe what to remember or forget about this client',
        emptyText:
            'No memory available yet. You can add notes below to help Phoebe learn about this client.',
    },
} as const;

type MemoryTabProps = {
    readonly organizationId: string;
    readonly entityId: string | null;
    readonly entityType: 'caregiver' | 'client';
};

function useMemoryData(
    entityType: 'caregiver' | 'client',
    organizationId: string,
    entityId: string | null,
) {
    const enabledOrgId = entityId ? organizationId : null;

    const caregiverResult = useCaregiverMemory(
        entityType === 'caregiver' ? enabledOrgId : null,
        entityType === 'caregiver' ? entityId : null,
        { reason: 'memory-tab' },
    );
    const clientResult = useClientMemory(
        entityType === 'client' ? enabledOrgId : null,
        entityType === 'client' ? entityId : null,
        { reason: 'memory-tab' },
    );

    return entityType === 'caregiver' ? caregiverResult : clientResult;
}

async function fetchMemory(
    entityType: 'caregiver' | 'client',
    organizationId: string,
    entityId: string,
) {
    if (entityType === 'caregiver') {
        return getApiClient().get({
            url: '/organizations/{organization_id}/caregivers/{caregiver_id}/memory',
            pathParams: {
                organization_id: organizationId,
                caregiver_id: entityId,
            },
        });
    }
    return getApiClient().get({
        url: '/organizations/{organization_id}/clients/{client_id}/memory',
        pathParams: {
            organization_id: organizationId,
            client_id: entityId,
        },
    });
}

async function compileMemory(
    entityType: 'caregiver' | 'client',
    organizationId: string,
    entityId: string,
) {
    if (entityType === 'caregiver') {
        return getApiClient().post({
            url: '/organizations/{organization_id}/caregivers/{caregiver_id}/memory/compile',
            pathParams: {
                organization_id: organizationId,
                caregiver_id: entityId,
            },
        });
    }
    return getApiClient().post({
        url: '/organizations/{organization_id}/clients/{client_id}/memory/compile',
        pathParams: {
            organization_id: organizationId,
            client_id: entityId,
        },
    });
}

async function submitInstruction(
    entityType: 'caregiver' | 'client',
    organizationId: string,
    entityId: string,
    instruction: string,
) {
    if (entityType === 'caregiver') {
        return getApiClient().post({
            url: '/organizations/{organization_id}/caregivers/{caregiver_id}/memory/instructions',
            pathParams: {
                organization_id: organizationId,
                caregiver_id: entityId,
            },
            body: { instruction },
        });
    }
    return getApiClient().post({
        url: '/organizations/{organization_id}/clients/{client_id}/memory/instructions',
        pathParams: {
            organization_id: organizationId,
            client_id: entityId,
        },
        body: { instruction },
    });
}

export function MemoryTab({
    organizationId,
    entityId,
    entityType,
}: MemoryTabProps) {
    const config = ENTITY_CONFIG[entityType];
    const {
        data: memory,
        isLoading,
        mutate,
    } = useMemoryData(entityType, organizationId, entityId);
    const [instruction, setInstruction] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isQueued, setIsQueued] = useState(false);

    const {
        canRecordAudio,
        isRecording,
        isTranscribing,
        voiceInputError,
        startRecording,
        stopRecording,
    } = useVoiceInput({
        organizationId,
        currentText: instruction,
        onTranscribed: setInstruction,
        composer: `${entityType}_memory`,
        voiceInputFlow: `${entityType}_memory_instruction`,
        disabled: !entityId || isSubmitting || isQueued,
    });
    const pollIntervalRef = useRef<number | null>(null);
    const pollTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) {
                window.clearInterval(pollIntervalRef.current);
            }
            if (pollTimeoutRef.current) {
                window.clearTimeout(pollTimeoutRef.current);
            }
        };
    }, []);

    const stopPolling = useCallback(
        (revalidate = false) => {
            if (pollIntervalRef.current) {
                window.clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
            if (pollTimeoutRef.current) {
                window.clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
            setIsQueued(false);
            if (revalidate) {
                void mutate();
            }
        },
        [mutate],
    );

    const startPollingForCompiledMemory = useCallback(
        (compiledAtBefore: string | null) => {
            if (!entityId) {
                return;
            }
            stopPolling();
            setIsQueued(true);

            pollIntervalRef.current = window.setInterval(async () => {
                try {
                    const response = await fetchMemory(
                        entityType,
                        organizationId,
                        entityId,
                    );
                    const compiledAtNow = response.compiled_at ?? null;
                    const compileFinished =
                        compiledAtBefore === null
                            ? compiledAtNow !== null
                            : compiledAtNow !== compiledAtBefore;

                    if (compileFinished) {
                        stopPolling();
                        await mutate(response, {
                            revalidate: false,
                        });
                    }
                } catch {
                    // Keep polling; transient errors
                    // should not fail the UX.
                }
            }, MEMORY_POLL_INTERVAL_MS);

            pollTimeoutRef.current = window.setTimeout(() => {
                stopPolling(true);
            }, MEMORY_POLL_TIMEOUT_MS);
        },
        [entityId, entityType, mutate, organizationId, stopPolling],
    );

    const handleSubmit = useCallback(async () => {
        if (!entityId || isSubmitting) {
            return;
        }

        const normalizedInstruction = instruction.trim();
        if (!normalizedInstruction) {
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await submitInstruction(
                entityType,
                organizationId,
                entityId,
                normalizedInstruction,
            );
            setInstruction('');
            startPollingForCompiledMemory(response.compiled_at_before ?? null);
        } catch {
            // Keep the form state intact so the user can retry immediately.
        } finally {
            setIsSubmitting(false);
        }
    }, [
        entityId,
        entityType,
        instruction,
        isSubmitting,
        organizationId,
        startPollingForCompiledMemory,
    ]);

    const handleCompile = useCallback(async () => {
        if (!entityId || isQueued || isSubmitting) {
            return;
        }
        try {
            const response = await compileMemory(
                entityType,
                organizationId,
                entityId,
            );
            startPollingForCompiledMemory(response.compiled_at_before ?? null);
        } catch {
            // Silently fail; user can retry.
        }
    }, [
        entityId,
        entityType,
        isQueued,
        isSubmitting,
        organizationId,
        startPollingForCompiledMemory,
    ]);

    const showCounter = instruction.length >= 400;
    const disableSubmit =
        isSubmitting ||
        isQueued ||
        isRecording ||
        isTranscribing ||
        instruction.trim().length === 0 ||
        !entityId;
    const disableCompile = isSubmitting || isQueued || !entityId;

    return (
        <div className="flex h-full flex-col overflow-hidden">
            <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold">Memory</div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disableCompile}
                    onClick={() => void handleCompile()}
                    className="h-7 gap-1.5 px-2 text-xs"
                >
                    <RefreshCw className="h-3 w-3" />
                    Recompile
                </Button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col pr-2">
                <div className="border-border flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border p-3">
                    <div className="h-full min-h-0 flex-1 overflow-x-hidden overflow-y-auto pr-1">
                        {isLoading || isQueued ? (
                            <div className="space-y-3">
                                <Skeleton className="h-4 w-36" />
                                <Skeleton className="h-4 w-full" />
                                <Skeleton className="h-4 w-11/12" />
                                <Skeleton className="h-4 w-9/12" />
                            </div>
                        ) : memory?.compiled_markdown ? (
                            <div className="text-foreground text-sm">
                                <Markdown>
                                    {stripCodeFences(memory.compiled_markdown)}
                                </Markdown>
                            </div>
                        ) : (
                            <div className="text-foreground text-sm">
                                {config.emptyText}
                            </div>
                        )}
                        {isQueued && (
                            <div className="text-muted-foreground mt-3 flex items-center gap-2 text-xs">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Updating memory…
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="mt-4 space-y-2">
                <div className="flex items-start gap-2">
                    <Textarea
                        id={config.inputId}
                        aria-label="Add memory instruction"
                        value={instruction}
                        onChange={(event) => setInstruction(event.target.value)}
                        placeholder={
                            isRecording ? 'Listening...' : config.placeholder
                        }
                        maxLength={MAX_INSTRUCTION_LENGTH}
                        disabled={isRecording || isTranscribing}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                void handleSubmit();
                            }
                        }}
                        className="min-h-9 resize-none"
                    />
                    {canRecordAudio ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            aria-label={
                                isTranscribing
                                    ? 'Transcribing voice input'
                                    : isRecording
                                      ? 'Stop recording'
                                      : 'Record voice input'
                            }
                            disabled={
                                isTranscribing ||
                                (!isRecording &&
                                    (!entityId || isSubmitting || isQueued))
                            }
                            onClick={() => {
                                if (isRecording) {
                                    stopRecording();
                                    return;
                                }
                                void startRecording();
                            }}
                        >
                            {isTranscribing ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <span className="relative flex items-center justify-center">
                                    <Mic className="h-4 w-4" />
                                    {isRecording ? (
                                        <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                                    ) : null}
                                </span>
                            )}
                        </Button>
                    ) : null}
                    <Button
                        type="button"
                        size="icon"
                        disabled={disableSubmit}
                        onClick={() => void handleSubmit()}
                        aria-label="Submit memory instruction"
                    >
                        {isSubmitting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <SendHorizontal className="h-4 w-4" />
                        )}
                    </Button>
                </div>
                {voiceInputError ? (
                    <p className="text-destructive text-xs">
                        {voiceInputError}
                    </p>
                ) : null}
                {showCounter ? (
                    <div className="text-muted-foreground text-right text-xs">
                        {instruction.length} / {MAX_INSTRUCTION_LENGTH}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
